import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(198, 40, 40, 0.05) 0%, rgba(142, 0, 0, 0.02) 100%)',
  padding: theme.spacing(15, 0, 10, 0),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(198, 40, 40, 0.1)',
}));

const ErrorCode = styled(Typography)(({ theme }) => ({
  fontSize: '8rem',
  fontWeight: 900,
  background: 'linear-gradient(135deg, #c62828 0%, #8e0000 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  lineHeight: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '6rem',
  },
}));

const PageNotFound = () => {
  return (
    <>
      <HeroSection>
        <Container maxWidth="md">
          <StyledPaper elevation={0}>
            {/* Icône */}
            <Box sx={{ mb: 4 }}>
              <SearchOffIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#c62828',
                  opacity: 0.8
                }} 
              />
            </Box>

            {/* Code d'erreur */}
            <ErrorCode variant="h1">
              404
            </ErrorCode>

            {/* Titre */}
            <Typography variant="h3" sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: '#333'
            }}>
              Page non trouvée
            </Typography>

            {/* Message */}
            <Typography variant="h6" sx={{ 
              color: '#666', 
              mb: 4,
              lineHeight: 1.6,
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Désolé, la page que vous cherchez n'existe pas ou a été déplacée.
              Vous pouvez retourner à l'accueil ou explorer nos formations.
            </Typography>

            {/* Actions */}
            <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
              <Grid item>
                <Button
                  component={RouterLink}
                  to="/"
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #c62828 0%, #8e0000 100%)',
                    color: 'white',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: '50px',
                    boxShadow: '0 4px 15px rgba(198, 40, 40, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #8e0000 0%, #c62828 100%)',
                      boxShadow: '0 6px 20px rgba(198, 40, 40, 0.4)',
                    }
                  }}
                >
                  Retour à l'accueil
                </Button>
              </Grid>
              <Grid item>
                <Button
                  component={RouterLink}
                  to="/formations"
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderColor: '#c62828',
                    color: '#c62828',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: '50px',
                    borderWidth: '2px',
                    '&:hover': {
                      borderColor: '#8e0000',
                      backgroundColor: 'rgba(198, 40, 40, 0.05)',
                      borderWidth: '2px',
                    }
                  }}
                >
                  Voir nos formations
                </Button>
              </Grid>
            </Grid>

            {/* Liens rapides */}
            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#666'
              }}>
                Vous cherchez peut-être :
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/about"
                  size="small"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#c62828',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  À propos
                </Button>
                <Button
                  component={RouterLink}
                  to="/boutique"
                  size="small"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#c62828',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  Boutique
                </Button>
                <Button
                  component={RouterLink}
                  to="/contact"
                  size="small"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#c62828',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  Contact
                </Button>
                <Button
                  component={RouterLink}
                  to="/emplacement"
                  size="small"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#c62828',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  Emplacement
                </Button>
              </Box>
            </Box>
          </StyledPaper>

          {/* Décorations */}
          <Box sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(198, 40, 40, 0.03) 0%, rgba(142, 0, 0, 0.01) 100%)',
            zIndex: -1,
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: '15%',
            right: '7%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(198, 40, 40, 0.02) 0%, rgba(142, 0, 0, 0.005) 100%)',
            zIndex: -1,
          }} />
        </Container>
      </HeroSection>
    </>
  );
};

export default PageNotFound;